// O DESAFIO DA SEMANA: todo mundo joga exatamente a mesma liga.
//
// ============================================================================
// POR QUE ISTO FUNCIONA AQUI, E QUASE SO AQUI
//
// O motor do 82 e deterministico por semente: `newLeague(liga, semente)` gera
// o mesmo mundo, com os mesmos elencos e o mesmo calendario, para qualquer
// pessoa em qualquer aparelho. Isso ja era verdade desde o M1 -- so nunca
// tinha sido usado para nada.
//
// Aqui vira o produto: uma semente por semana, um cenario curto, e o resultado
// de todo mundo comparavel porque ninguem jogou um mundo diferente. Um jogo de
// gerenciamento normalmente nao consegue fazer isso sem reescrever o motor.
//
// ============================================================================
// AS DECISOES, E O MOTIVO DE CADA UMA
//
// SEMPRE NO NBB. A liga gratuita. Um desafio que so quem pagou pode jogar nao
// serve para trazer gente -- e trazer gente e a razao de o desafio existir.
//
// SEMPRE CURTO (10 rodadas). Uma carreira e de 34 a 82 rodadas e nao cabe no
// intervalo do almoco. O desafio tem que caber, senao ninguem repete na
// semana seguinte.
//
// A SEMANA COMECA NA SEGUNDA, NO FUSO DO BRASIL. Usar UTC faria a semana virar
// as 21h de domingo -- o mesmo defeito que o painel CEO teve ate 05/09/2026.
// ============================================================================


import { sortedStandings } from './season.js';

/* A semana, os cenarios e o sorteio moram em /semana.js -- sem imports, para
   o servidor poder usar sem carregar o motor. Reexportados aqui para quem le o
   motor achar tudo num lugar so. */
export {
  LIGA_DO_DESAFIO, RODADAS_DO_DESAFIO, CENARIOS, semanaDe, desafioDaSemana,
} from '../../semana.js';

/**
 * Monta o estado inicial do desafio: a liga corre sozinha ate a rodada de
 * partida, e ai a pessoa assume.
 *
 * ⚠️ RECEBE AS FUNCOES DE CARREIRA POR PARAMETRO em vez de importar
 * `career.js`. O career.js ja importa dezenas de modulos, e importar ele de
 * volta aqui fecharia um ciclo -- que no Vite nao explode, so devolve
 * `undefined` em silencio na hora errada.
 */
export function montarDesafio(desafio, { newLeague, createCareer, advanceRound, autoLineup }) {
  const liga = newLeague(desafio.liga, desafio.semente);

  /* Um clube qualquer para comecar: quem vai jogar e decidido DEPOIS, pela
     tabela. Simular a liga exige um dono, e nesta fase ele e so um lugar. */
  let s = createCareer({ league: liga, clubId: liga.teams[0].id, coachName: 'Voce' });
  const rodadaInicial = s.schedule.length - desafio.rodadas + 1;

  s = autoLineup(s).state;
  for (let i = 1; i < rodadaInicial; i++) s = advanceRound(s).state;

  // Agora sim: quem esta na posicao que o cenario pede?
  const tabela = sortedStandings(s.standings);
  const idx = desafio.posicao < 0
    ? tabela.length + desafio.posicao
    : desafio.posicao - 1;
  const alvo = tabela[Math.max(0, Math.min(tabela.length - 1, idx))];

  /* ⚠️ Monta a carreira DE NOVO, agora com o clube certo, e resimula. Trocar
     `clubId` no estado pronto seria mais rapido e estaria errado: o elenco do
     usuario e montado com regras proprias (rotacao automatica), e o clube que
     rodou como IA ate aqui nao passou por elas. Resimular garante que o mundo
     de todo mundo e identico ao meu. */
  const liga2 = newLeague(desafio.liga, desafio.semente);
  let jogo = createCareer({ league: liga2, clubId: alvo.teamId, coachName: 'Voce' });
  jogo = autoLineup(jogo).state;
  for (let i = 1; i < rodadaInicial; i++) jogo = advanceRound(jogo).state;

  return {
    estado: jogo,
    rodadaInicial,
    clubeId: alvo.teamId,
    clubeNome: jogo.teams.find((t) => t.id === alvo.teamId)?.name || alvo.teamId,
    posicaoInicial: tabela.findIndex((r) => r.teamId === alvo.teamId) + 1,
    campanhaInicial: { wins: alvo.wins, losses: alvo.losses },
  };
}

/**
 * A pontuacao: vitorias nas rodadas do desafio, saldo como desempate.
 *
 * ⚠️ Simples de proposito. Ja tentei escrever uma formula com posicao final,
 * saldo e aproveitamento ponderados -- e ninguem consegue olhar para o ranking
 * e entender por que ficou atras de alguem. "Quantas voce venceu" cabe numa
 * frase, e um placar que nao se explica sozinho nao gera conversa.
 */
export function pontuar(estadoInicial, estadoFinal, clubeId) {
  const antes = estadoInicial.standings[clubeId] || { wins: 0, losses: 0 };
  const depois = estadoFinal.standings[clubeId] || { wins: 0, losses: 0 };
  const vitorias = (depois.wins || 0) - (antes.wins || 0);
  const derrotas = (depois.losses || 0) - (antes.losses || 0);

  // O saldo das rodadas do desafio, e nao o da temporada inteira.
  let saldo = 0;
  for (const r of estadoFinal.results) {
    if (r.round < estadoInicial.round) continue;
    if (r.home === clubeId) saldo += r.hs - r.as;
    else if (r.away === clubeId) saldo += r.as - r.hs;
  }

  const tabela = sortedStandings(estadoFinal.standings);
  return {
    vitorias,
    derrotas,
    saldo,
    posicaoFinal: tabela.findIndex((x) => x.teamId === clubeId) + 1,
  };
}

/** A ordem do ranking: mais vitorias, depois melhor saldo. */
export const compararResultados = (a, b) => (b.vitorias - a.vitorias) || (b.saldo - a.saldo);
